import Dropdown from "@/lib/Dropdown";
import { CadModelFile, CadNode } from "@/lib/queries/types";
import { Tooltip } from "react-tooltip";
import { truncateTextMiddle } from "@/lib/utils";
import { Dispatch, SetStateAction } from "react";
import { components } from "@/lib/ApiSchema";
import { PendingSavePayload } from "./PartTreeItem";
import CaretDownIcon from "@/lib/CaretDownIcon";
import classnames from "classnames";
import AlertIcon from "~icons/local/alert";

export default function SelectCadModel({
  cadModel,
  cadModels,
  pendingSave,
  setPendingSave,
  disabled = false,
  truncateThreshold,
}: {
  cadModel: components["schemas"]["PartCadModelSchema"] | null;
  cadModels: CadModelFile[];
  pendingSave: PendingSavePayload | undefined;
  setPendingSave: Dispatch<SetStateAction<PendingSavePayload | undefined>>;
  disabled?: boolean;
  truncateThreshold: number;
}) {
  let selectedCadModelNode: {
    fileId: string;
    nodeId: number | null | undefined;
  } | null = null;

  if (pendingSave?.cad_model_id && pendingSave.cad_model_node_id) {
    selectedCadModelNode = {
      fileId: pendingSave.cad_model_id,
      nodeId: pendingSave.cad_model_node_id,
    };
  } else if (cadModel) {
    selectedCadModelNode = {
      fileId: cadModel.id,
      nodeId: cadModel?.node_id,
    };
  }

  const options = cadModels.flatMap((file) => flatCadModelNodes(file));
  const tooltipId = Math.random().toString();
  const fullSelectedLabel = display();
  const truncatedSelectedLabel = truncateTextMiddle(
    fullSelectedLabel,
    truncateThreshold,
  );

  return (
    <Dropdown
      disabled={disabled}
      trigger={({ expanded }: { expanded: boolean }) => (
        <div
          data-tooltip-id={tooltipId}
          className={classnames(
            "cursor-pointer rounded-lg flex gap-2 px-2 py-1 justify-between items-center",
            {
              "text-gray-500 bg-red-100": selectedCadModelNode === null,
            },
          )}
        >
          {selectedCadModelNode === null && (
            <AlertIcon className="text-red-500 size-4" />
          )}
          <div>{truncatedSelectedLabel}</div>
          {!disabled && <CaretDownIcon expanded={expanded} />}
          {fullSelectedLabel !== truncatedSelectedLabel && (
            <Tooltip id={tooltipId}>
              <span className="text-xs">{fullSelectedLabel}</span>
            </Tooltip>
          )}
        </div>
      )}
    >
      <div className="flex flex-col text-xs bg-white p-2 my-2 overflow-y-auto max-h-80 max-w-80">
        {options.map(({ label, nodeId, fileId, level }, i) => {
          const invalid = nodeId === null;
          return (
            <div
              key={i}
              className={
                fileId === selectedCadModelNode?.fileId &&
                nodeId === selectedCadModelNode.nodeId
                  ? "bg-slate-100"
                  : ""
              }
            >
              {invalid && (
                <Tooltip id={fileId}>File processing failed.</Tooltip>
              )}
              <div
                className={`rounded-sm px-4 whitespace-nowrap ${invalid ? "cursor-not-allowed opacity-50" : "hover:bg-slate-100 cursor-pointer"} select-none flex`}
                data-tooltip-id={fileId}
                onClick={() => {
                  if (invalid) return;
                  if (
                    fileId !== selectedCadModelNode?.fileId ||
                    nodeId !== selectedCadModelNode.nodeId
                  ) {
                    setPendingSave((pre) => {
                      return {
                        ...pre,
                        cad_model_id: fileId,
                        cad_model_node_id: nodeId,
                      };
                    });
                  }
                }}
              >
                {[...Array(level).keys()].map((k) => (
                  <div key={k} className="w-4 border-l" />
                ))}
                <div className="py-2 truncate" title={label}>
                  {label}
                </div>
              </div>
            </div>
          );
        })}
        {options.length === 0 && <div>No File</div>}
      </div>
    </Dropdown>
  );

  function display() {
    if (!selectedCadModelNode)
      return disabled ? "No file selected" : "Choose a model file";
    const nodeLabel = getNodeLabel();
    const fileName =
      options
        .filter(({ level }) => level === 0)
        .find(({ fileId }) => fileId === selectedCadModelNode.fileId)?.label ??
      "";
    if (!nodeLabel) return fileName;
    return `${fileName} > ${nodeLabel}`;
  }

  function getNodeLabel() {
    if (!selectedCadModelNode) return null;
    if (typeof selectedCadModelNode.nodeId !== "number") return null;
    const selected = options
      .filter(({ fileId }) => fileId === selectedCadModelNode.fileId)
      .find(({ nodeId }) => nodeId === selectedCadModelNode.nodeId);
    if (!selected || selected.level === 0) return null;
    return selected.label;
  }
}

type CadModelOption = {
  fileId: string;
  nodeId: number | null;
  level: number;
  label: string;
};

function flatCadModelNodes(cadModel: CadModelFile): CadModelOption[] {
  const { hierarchy_root, file_name, id } = cadModel;
  if (!hierarchy_root) {
    return [
      {
        fileId: id,
        nodeId: null,
        level: 0,
        label: file_name,
      },
    ];
  }

  const root = { ...hierarchy_root, label: file_name };
  return flatTree(root).map((node) => ({
    ...node,
    fileId: id,
  }));
}

function flatTree(
  root: CadNode,
  level: number = 0,
): { nodeId: number; label: string; level: number }[] {
  return root.children.reduce(
    (nodeList, node) => [...nodeList, ...flatTree(node, level + 1)],
    [{ nodeId: root.id, label: root.label, level }],
  );
}
